import { Component, inject, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFormValidators } from '../../../../shared/validators/custom-form-validators';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { IndividualData } from '@models/individuals/individual-data';
import { Relationship, ShareholderRelationshipDetails } from '@models/relationships/relationship';
import { CorporateHolderModel, SecurityRegistryRecord, } from '@models/shares/security-registry-record';
import { SelectOption } from '@models/ui-models/select-options';
import { IStep } from '@models/ui-models/step';
import { SecurityApiService } from '@shared/services/security-api.service';
import { PaymentMethod, paymentMethodOptions } from '../../../models/company/company-change-name.model';
import {
  CompanyChangeDeregister,
  voluntaryDeregistrationDeclarations,
  VoluntaryDeregistrationStepsEnum
} from '../../../models/company/company-change-deregister.model';
import { EntityChangeData } from "@models/documents/entity-change-data";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import {
  IndividualDataFormGroupComponent,
  IndividualDataFormGroupControls
} from "../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component";
import { parseFullName } from "../../../../shared/utils/parse-fullname";
import { CompanyNameChangeStepsEnum } from "../company-change-name/company-change-name.component";
import {
  AuthorisedSignatoriesStepComponent,
  IAuthorisedSignatoriesFormGroup
} from "../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component";
import { ParsedName } from "@models/individuals/parsed-name";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { filterRecordsWithEmptyHoldings } from "../../../utils/filter-records-with-empty-holdings";

export interface DetailsOfTheApplicantControls {
  isApplicantDirector: FormControl<boolean | null>,
  paymentMethod: FormControl<PaymentMethod>,
  applicantIfNominee: FormGroup<Partial<IndividualDataFormGroupControls>>,

  // Director Selected
  applicantIfDirector: FormControl<string | null>,
}

export const isApplicantDirectorOptions: SelectOption[] = [
  { label: 'Director', value: true },
  { label: 'Company', value: false }
];

export const VoluntaryDeregistrationSteps: IStep<VoluntaryDeregistrationStepsEnum>[] = [
  { step: VoluntaryDeregistrationStepsEnum.FormDescription, label: 'Form Description' },
  { step: VoluntaryDeregistrationStepsEnum.ApplicantDeclaration, label: 'Applicant Declaration' },
  { step: VoluntaryDeregistrationStepsEnum.DetailsOfTheApplicant, label: 'Details of the Applicant' },
  { step: VoluntaryDeregistrationStepsEnum.AuthorisedSignatories, label: 'Authorised Signatories' },
];

@Component({
  selector: 'app-form6010-voluntary-deregistration',
  standalone: true,
  templateUrl: './company-change-deregister.component.html',
  imports: [
    StepperFormComponent,
    ReactiveFormsModule,
    StepperFormDescriptionComponent,
    CheckboxComponent,
    RadioComponent,
    SelectComponent,
    IndividualDataFormGroupComponent,
    AuthorisedSignatoriesStepComponent
  ],
  styleUrls: [
    './company-change-deregister.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  ]
})
export class CompanyChangeDeregisterComponent extends BaseStepperFormComponent<VoluntaryDeregistrationStepsEnum, CompanyChangeDeregister> implements OnInit {
  securityService = inject(SecurityApiService);

  @Input() officers: Record<string, Relationship[]> = {};
  @Input() directorsOptions: SelectOption[] = [];

  securityRegistryRecords: SecurityRegistryRecord[] = [];
  corporateHolders: CorporateHolderModel[] = [];

  override readonly StepsEnum = VoluntaryDeregistrationStepsEnum;
  override steps = VoluntaryDeregistrationSteps;
  override currentStepIndex = 0;
  override currentStep = this.steps[0].step;
  readonly ShareholderRelationshipDetails = ShareholderRelationshipDetails;
  readonly isApplicantDirectorOptions = isApplicantDirectorOptions;
  readonly paymentMethodOptions: SelectOption[] = paymentMethodOptions;
  readonly disabledIndividualControlKeys: (keyof IndividualDataFormGroupControls)[] = ['formerName', 'dob', 'birthCity', 'birthCountry'];
  readonly voluntaryDeregistrationDeclarations = voluntaryDeregistrationDeclarations;

  stepsSignal: WritableSignal<IStep<VoluntaryDeregistrationStepsEnum>[]> = signal(VoluntaryDeregistrationSteps);

  override stepperForm = new FormGroup({
    [VoluntaryDeregistrationStepsEnum.FormDescription]: new FormGroup({}),
    [VoluntaryDeregistrationStepsEnum.ApplicantDeclaration]: new FormGroup({
      declarationNoAssetsLiabilities: new FormControl<boolean>(false, [CustomFormValidators.valueMatchValidator(true)])
    }),
    [VoluntaryDeregistrationStepsEnum.DetailsOfTheApplicant]: new FormGroup({
      isApplicantDirector: new FormControl<boolean | null>(null, [Validators.required]),
      paymentMethod: new FormControl<PaymentMethod>(PaymentMethod.INV, [Validators.required]),

      // Company Selected
      applicantIfNominee: IndividualDataFormGroupComponent.defineForm(),
      // Director Selected
      applicantIfDirector: new FormControl<string | null>(null, [Validators.required]),
    }) as FormGroup<DetailsOfTheApplicantControls>,
    [CompanyNameChangeStepsEnum.AuthorisedSignatories]: AuthorisedSignatoriesStepComponent.defineForm(),
  });

  constructor() {
    super();
    this.redirectAfterSubmit = true;
  }

  ngOnInit(): void {
    this.loadNboHolders();
    this.listenApplicantTypeChanges();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeDeregister);
  }

  override setupChange(change: CompanyChangeDeregister = this.formModel): void {
    if (!this.isEdit && change === this.formModel)
      return;

    const applicantIfDirector = change.applicantIfDirector?.key
      || this.directorsOptions.find((option) => option.label.trim() === change.applicantIfDirector?.individualDataOverride?.fullName?.trim())?.value as string
      || null;
    this.applicantDeclarationForm.controls.declarationNoAssetsLiabilities.patchValue(change.declarationNoAssetsLiabilities);
    this.detailsOfTheApplicantForm.patchValue({
      ...change,
      applicantIfDirector: applicantIfDirector ?? null,
      applicantIfNominee: change.applicantIfNominee ?? {},
    });
  }

  override buildDocument(): Document | null {
    const applicantIfDirector = this.detailsOfTheApplicantForm.value.applicantIfDirector ? this.officers[this.detailsOfTheApplicantForm.value.applicantIfDirector][0] : undefined;
    const applicantIfNominee = this.detailsOfTheApplicantForm.value.applicantIfNominee ?
      new IndividualData({
        ...this.detailsOfTheApplicantForm.value?.applicantIfNominee as Partial<IndividualData>,
        ...parseFullName(this.detailsOfTheApplicantForm.controls.applicantIfNominee.controls.fullName?.value ?? ''),
        formerName: parseFullName(this.detailsOfTheApplicantForm.controls.applicantIfNominee.controls.formerFullName?.value ?? '') as ParsedName,
      })
      : undefined;

    const changes = new CompanyChangeDeregister({
      ...this.detailsOfTheApplicantForm.value as Partial<CompanyChangeDeregister>,
      ...this.applicantDeclarationForm.value as Partial<CompanyChangeDeregister>,
      applicantIfNominee,
      applicantIfDirector,
      changeDate: DatepickerHelper.buildTodayDateString(),
    });

    try {
      return new Document({
        changes: [changes],
        entityId: this.companyChangeData.entityId,
        type: 'c:6010',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  private listenApplicantTypeChanges(): void {
    this.detailsOfTheApplicantForm.controls.isApplicantDirector.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((applicantType: boolean | null) => {
        setControlDisabled(this.detailsOfTheApplicantForm.controls.applicantIfDirector, applicantType === false);
        setControlDisabled(this.detailsOfTheApplicantForm.controls.applicantIfNominee, applicantType === true);

        if (applicantType === false) {
          // is Company

          this.disabledIndividualControlKeys.forEach(key => {
            setControlDisabled(this.detailsOfTheApplicantForm.controls.applicantIfNominee?.controls[key]);
          });
        } else if (applicantType === true) {
          // is Director
        }
      });
  }

  private updateFormSteps(): void {
    const steps = VoluntaryDeregistrationSteps.filter((step) => {

      if (step.step === VoluntaryDeregistrationStepsEnum.AuthorisedSignatories) {
        return this.corporateHolders.length;
      }

      return true;
    });

    this.stepsSignal.set(steps);
  }

  private loadNboHolders(): void {
    this.isLoading = true;

    this.securityService.getSecurityRegistryRecords(this.companyChangeData.entityId)
      .subscribe((securityHolderRecords) => {
        const records = filterRecordsWithEmptyHoldings(securityHolderRecords);
        this.securityRegistryRecords = records;
        this.corporateHolders = records
          .flatMap((record) => record.holders.filter(h => h.$type === CorporateHolderModel.$type)) as CorporateHolderModel[];

        this.updateFormSteps();
        this.setupChange();
        this.isLoading = false;
      });
  }

  get applicantDeclarationForm() {
    return this.stepperForm.controls[VoluntaryDeregistrationStepsEnum.ApplicantDeclaration];
  }

  get detailsOfTheApplicantForm() {
    return this.stepperForm.controls[VoluntaryDeregistrationStepsEnum.DetailsOfTheApplicant];
  }

  get authorisedSignatoriesForm(): IAuthorisedSignatoriesFormGroup {
    return this.stepperForm.controls[VoluntaryDeregistrationStepsEnum.AuthorisedSignatories];
  }

  override setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.stepsSignal()[newStepIndex].step;
    this.currentStepIndex = newStepIndex;
  }
}
